import { Component, computed, DestroyRef, effect, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmAutocompleteImports, provideHlmAutocompleteConfig } from '@spartan-ng/helm/autocomplete';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { FormControlNames } from '../../../../référentiel-métier-parcours/models/form-control-names.model';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { LabelRequiredDirective } from '../../../../shared/directives/label-required.directive';
import { Site } from '../../model/site.model';
import { SiteCreateRequest } from '../../request/create-request';
import { RégionStore } from '../../store/région.store';
import { SiteStore } from '../../store/site.store';

@Component({
  selector: 'app-form-site',
  imports: [
    ReactiveFormsModule,
    HlmFieldImports,
    ButtonAddNewComponent,
    HlmInputImports,
    HlmAutocompleteImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-site.component.html',
  providers: [
    provideHlmAutocompleteConfig({
      transformOptionToString: (option: Site) => option.nom,
      transformValueToSearch: (option: Site) => option.nom,
    }),
  ],
})
export class FormSiteComponent implements OnInit {
  readonly #régionStore = inject(RégionStore);
  readonly #siteStore = inject(SiteStore);
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);

  readonly cartographie = input.required<Cartographie>();
  readonly site = input<Site>();

  readonly regions = computed(() => this.#régionStore.entities());

  public readonly search = signal('');

  public readonly filteredRegions = computed(() =>
    this.#régionStore.entities().filter((option) => option.nom.toLowerCase().includes(this.search().toLowerCase())),
  );

  form = this.#fb.group({
    [FormControlNames.REGION]: [null, [Validators.required]],
    [FormControlNames.NOMS]: this.#fb.array([]),
  });

  constructor() {
    effect(() => this.#régionStore.charger(this.cartographie().id));

    effect(() => {
      const site = this.site();

      if (site) {
        this.region.setValue({ nom: site.region });
        this.noms.clear();
        this.ajouterSite(site);
      }
    });
  }

  ngOnInit(): void {
    this.ajouterSite();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  ajouterNouvelleRegion() {
    this.region.setValue({ nom: this.search() });
  }

  ajouterSite(site?: Site) {
    const siteForm = this.#fb.control(site?.nom, [Validators.required]);
    this.noms.push(siteForm);
  }

  onSubmit() {
    const toastrId = uuidv7();

    if (this.form.invalid) return;

    const site = this.site();
    const region = this.region.value.nom;
    const noms = this.noms.value as string[];

    if (site) {
      const nom = noms[0];
      const siteModifié = { ...site, nom, region };
      this.#siteStore.modifier({
        site: siteModifié,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: `Modification du CFA ${nom} réussie`,
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
        },
      });
    } else {
      const data: SiteCreateRequest = {
        idCartographie: this.cartographie().id,
        region,
        noms,
      };

      this.#siteStore.ajouter({
        data,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: 'Création des CFA réussie',
              id: toastrId,
            },
            id: toastrId,
          });
          this.#siteStore.charger(this.cartographie().id);
          this.#sidepanelService.close();
        },
      });
    }
  }

  get region() {
    return this.form.get(FormControlNames.REGION) as FormControl;
  }

  get noms() {
    return this.form.get(FormControlNames.NOMS) as FormArray;
  }
}
